import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Tooltip } from '@mui/material';
import { theme } from '../../Theme';

export type ArcTweenProps = {
  occupied: number;
  total: number;
  name: string;
  description?: string;
};

export const ArcTween: React.FC<ArcTweenProps> = (props: ArcTweenProps) => {
  const { occupied, total, name, description = '' } = props;
  const arcTweenRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    const svg = d3.select(arcTweenRef.current);
    const width = 160;
    const height = 160;
    const radius = Math.min(width, height) / 2;

    // Clear existing elements
    svg.selectAll('*').remove();

    const foregroundArc = d3
      .arc()
      .innerRadius(50)
      .outerRadius(radius - 10)
      .startAngle(0);

    // Define drop shadow filter
    const defs = svg.append('defs');
    const filter = defs
      .append('filter')
      .attr('id', 'drop-shadow')
      .attr('height', '150%')
      .attr('width', '150%')
      .attr('x', '-25%')
      .attr('y', '-25%');

    filter
      .append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 3)
      .attr('result', 'blur');

    filter
      .append('feOffset')
      .attr('in', 'blur')
      .attr('dx', 0)
      .attr('dy', 0)
      .attr('result', 'offsetBlur');

    filter
      .append('feFlood')
      .attr('flood-color', 'gray')
      .attr('flood-opacity', 0.1)
      .attr('result', 'offsetColor');

    filter
      .append('feComposite')
      .attr('in2', 'offsetBlur')
      .attr('operator', 'in')
      .attr('in', 'color')
      .attr('result', 'shadow');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Draw background circle
    svg
      .append('circle')
      .attr('cx', width / 2)
      .attr('cy', height / 2)
      .attr('r', 60)
      .style('fill', theme.palette.grey[200])
      .style('filter', 'url(#drop-shadow)');

    const foreground = svg
      .append('path')
      .datum({ endAngle: 0 })
      .style('fill', theme.palette.primary.main)
      .attr('d', foregroundArc as any)
      .attr('transform', `translate(${width / 2},${height / 2})`);

    foreground
      .transition()
      .duration(2000)
      .attrTween('d', arcTween((occupied / total) * 2 * Math.PI));

    function arcTween(newAngle: number) {
      return function (d: any) {
        const interpolate = d3.interpolate(d.endAngle, newAngle);
        return function (t: number) {
          d.endAngle = interpolate(t);
          return foregroundArc(d) as any;
        };
      };
    }
  }, [occupied, total]);
  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-col items-center justify-center">
        <svg ref={arcTweenRef} width={160} height={160} />
        <Tooltip title={description}>
          <div className="absolute text-[#951D1D] font-bold">{`${occupied}/${total}`}</div>
        </Tooltip>
      </div>

      <div className="text-[#951D1D] font-bold">{name}</div>
    </div>
  );
};
